import { OperationVariables, useLazyQuery, useMutation } from '@apollo/client'
import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import debounce from 'lodash.debounce'
import isEqual from 'lodash.isequal'
import { useCallback, useContext, useMemo, useState } from 'react'

import { SetTableViewSettings } from 'bpm/api/graphQL/mutations/productListing.mutation'
import { GetTableViewSettings } from 'bpm/api/graphQL/queries/listing.queries'
import { ProductSearchQuery } from 'bpm/api/graphQL/queries/productSearch.queries'
import { ProductSearchResponse } from 'bpm/api/graphQL/types/product.types'
import { IdTitleProps } from 'bpm/components/SpecTable/settings/TableSettings'
import {
    DEFAULT_TABLE_SETTINGS,
    TableSettingsState,
    useTableSettingsStore,
} from 'bpm/components/SpecTable/settings/store'
import { availableColumnKeys, columnMap } from 'bpm/pages/TablePage/tablePage'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'

import {
    isDeltekUserorAdmin,
    isUserBPMAdmin,
} from 'bpm/pages/ProductsPage/productsUtils'
import { processEnv } from 'components/configuration/environmentVariables'
import { defaultTableSettings } from './ProductListingsPage.constants'

export type SelectedColumnType = {
    columnName: string
    order: number
    columnWidth: number
    isColumnPinned: boolean
    columnOrder: string | undefined
}

export const prepareTableSettingsFromData = (
    tableData: any,
    isAdminAccess?: boolean,
    isBPMReviewer?: boolean
) => {
    let selectedColumnsSettings: IdTitleProps[] = []
    let groupColumnsSettings: IdTitleProps[] = []
    let pinningPropsSettings = {
        firstColumnPinned: false,
        lastColumnPinned: false,
    }
    let collapseColumnCheckedSettings: Record<string, boolean> = {}
    let orderBySettings = ''
    let columnWidthsSettings: Record<string, number> = {}
    const groupEnabledSettings = tableData.isTableGroupingEnabled
    const isTableView = tableData.isTableView

    tableData.tableGroupings.forEach(
        ({
            columnName,
            isCollapsed,
        }: {
            columnName: string
            isCollapsed: boolean
        }) => {
            if (columnMap[columnName]?.Header) {
                groupColumnsSettings.push({
                    id: columnName,
                    title: (columnMap[columnName].Header as string) ?? '',
                })
                orderBySettings = `${orderBySettings}${columnName},`
            }

            collapseColumnCheckedSettings[columnName ?? ''] =
                isCollapsed ?? false
        }
    )

    tableData.selectedColumns.forEach(
        (
            {
                columnName,
                columnOrder,
                columnWidth,
                isColumnPinned,
            }: SelectedColumnType,
            index: number
        ) => {
            const normalizedColumnName =
                columnName === 'sectionNumber' ? 'sectionName' : columnName
            if (
                normalizedColumnName !== 'groupColExpander' &&
                !tableData.tableGroupings.some(
                    (group: Record<string, string>) =>
                        group.columnName === normalizedColumnName
                )
            ) {
                selectedColumnsSettings.push({
                    id: normalizedColumnName,
                    title:
                        (columnMap[normalizedColumnName]?.Header as string) ??
                        '',
                })
            }

            if (isColumnPinned) {
                if (index === 0) {
                    pinningPropsSettings.firstColumnPinned = true
                }
                if (index === tableData.selectedColumns.length - 1) {
                    pinningPropsSettings.lastColumnPinned = true
                }
            }

            const columnOrderString =
                `${columnOrder}`.toLowerCase() === 'undefined'
                    ? undefined
                    : `${columnOrder}`.toLowerCase() === 'descending'
                    ? ' desc,'
                    : ','

            if (columnOrderString) {
                orderBySettings = `${orderBySettings}${normalizedColumnName}${columnOrderString}`
            }

            columnWidthsSettings[normalizedColumnName] = Number(
                columnWidth ?? 0
            )
        }
    )

    const {
        hasProductDetailNotes,
        listingId,
        firmId,
        firmLogoUrl,
        ...filteredColumns
    } = columnMap

    let availableColumns = Object.values(
        !isAdminAccess ? filteredColumns : columnMap
    )
        .map((col: any) => ({ id: col.id, title: col.Header })) // Need to set this as any since Id types are clashing
        .filter(
            (col) =>
                !selectedColumnsSettings
                    .map((col) => col.id)
                    .includes(col.id) &&
                !groupColumnsSettings.map((col) => col.id).includes(col.id) &&
                (!!Number(processEnv.REACT_APP_ShowSwatchbox) ||
                    (!!!Number(processEnv.REACT_APP_ShowSwatchbox) &&
                        col.id !== 'partnershipLinksSwatchbox')) &&
                (!!Number(processEnv.REACT_APP_ShowContentLibraryOptions) ||
                    (!!!Number(
                        processEnv.REACT_APP_ShowContentLibraryOptions
                    ) &&
                        col.id !== 'contentLibrary'))
        )
        .sort(
            (a, b) =>
                availableColumnKeys.indexOf(a.id) -
                availableColumnKeys.indexOf(b.id)
        )

    if (isBPMReviewer) {
        availableColumns = availableColumns.filter(
            (col) => col.id !== 'isActive'
        )
        selectedColumnsSettings = selectedColumnsSettings.filter(
            (col) => col.id !== 'isActive'
        )
    }

    const sortType = orderBySettings.includes('type')
    if (!sortType) {
        orderBySettings = `${orderBySettings}type,`
    }

    const sortLastUpdated = orderBySettings.includes('lastUpdated')
    if (!sortLastUpdated) {
        orderBySettings = `${orderBySettings}lastUpdated desc`
    }

    return {
        availableColumns,
        groupEnabledSettings,
        selectedColumnsSettings,
        groupColumnsSettings,
        pinningPropsSettings,
        collapseColumnCheckedSettings,
        orderBySettings,
        columnWidthsSettings,
        isTableView,
    }
}

const useListingQueryHelper = () => {
    const currentUserInfoResponse = useContext(AuthContext)

    const {
        currentUserInfo: {
            account: { accountId: userId },
        },
    } = currentUserInfoResponse

    const { isAdminAccess, isBPMReviewer } = useMemo(() => {
        const {
            isDeltekAdmin,
            isDeltekPublishingAuthor,
            isDeltekAuthor,
            isDeltekReviewer,
        } = isDeltekUserorAdmin(currentUserInfoResponse)

        const { isBPMReviewer } = isUserBPMAdmin(currentUserInfoResponse)

        return {
            isBPMReviewer,
            isAdminAccess:
                isDeltekAdmin ||
                isDeltekPublishingAuthor ||
                isDeltekAuthor ||
                isDeltekReviewer,
        }
    }, [currentUserInfoResponse])

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const collapseColumnChecked = useTableSettingsStore(
        (state) => state.collapseColumnChecked
    )
    const setIsTableSettingLoaded = useTableSettingsStore(
        (state) => state.setIsTableSettingLoaded
    )
    const isTableSettingLoaded = useTableSettingsStore(
        (state) => state.isTableSettingLoaded
    )
    const setTableSettings = useTableSettingsStore(
        (state) => state.setTableSettings
    )

    const [searchLoading, setSearchLoading] = useState(false)
    const [isCardViewActive, setToggleCardView] = useState(true)
    const [saveQueryObject, setSaveQueryObject] = useState(
        {} as OperationVariables
    )

    const [
        showBottomLoadingIndicator,
        setShowBottomLoadingIndicator,
    ] = useState(false)

    const [showTableLoadingIndicator, setShowTableLoadingIndicator] = useState(
        false
    )

    const [saveTableData] = useMutation(SetTableViewSettings, {
        onCompleted: () => {
            setIsTableSettingLoaded(true)
        },
        onError: () => {
            // noop
        },
    })

    const saveTableSettings = useCallback(
        debounce(
            (options: OperationVariables) => {
                if (!isEqual(options, saveQueryObject)) {
                    saveTableData(options)
                    setSaveQueryObject(options)
                }
            },
            300,
            { trailing: true }
        ),
        [saveQueryObject, saveTableData]
    )

    const [getTableSettings] = useLazyQuery(GetTableViewSettings, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            const tableSettingsData = data?.getTableViewSettingOfUser

            if (tableSettingsData) {
                updateSettingsState(tableSettingsData)
                return
            } else {
                setTableSettings({
                    ...DEFAULT_TABLE_SETTINGS,
                    isTableSettingLoaded: true,
                })
            }
        },
        onError: () => {
            setTableSettings({
                ...DEFAULT_TABLE_SETTINGS,
                isTableSettingLoaded: true,
            })
        },
    })

    const [
        getListings,
        {
            data: searchData,
            fetchMore: searchFetchMore,
            called: searchCalled,
            loading: listingLoading,
            networkStatus: listingNetworkStatus,
        },
    ] = useLazyQuery<ProductSearchResponse>(ProductSearchQuery, {
        displayName: 'ProductSearchResult',
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
        onCompleted: () => {
            setSearchLoading(false)
            setShowTableLoadingIndicator(false)
            const hasExpandedColumns = Object.values(
                collapseColumnChecked
            ).some((value) => value === false)

            hasExpandedColumns
                ? setTimeout(() => {
                      setShowBottomLoadingIndicator(false)
                  }, 0)
                : setShowBottomLoadingIndicator(false)
        },
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Error on product search',
                    variant: 'error',
                    id: `ProductListingsPage_ProductSearchError`,
                },
            })
            setSearchLoading(false)
            setShowTableLoadingIndicator(false)
            setShowBottomLoadingIndicator(false)
        },
    })

    const updateSettingsState = useCallback(
        (tableData) => {
            const {
                availableColumns,
                groupEnabledSettings,
                selectedColumnsSettings,
                groupColumnsSettings,
                pinningPropsSettings,
                collapseColumnCheckedSettings,
                orderBySettings,
                columnWidthsSettings,
                isTableView,
            } = prepareTableSettingsFromData(
                tableData,
                isAdminAccess,
                isBPMReviewer
            )

            setTableSettings({
                availableColumns,
                groupEnabled: groupEnabledSettings,
                selectedColumns: selectedColumnsSettings,
                groupColumns: groupColumnsSettings,
                pinningProps: pinningPropsSettings,
                collapseColumnChecked: collapseColumnCheckedSettings,
                orderBy: orderBySettings,
                isTableView,
                columnWidths: columnWidthsSettings,
                isTableSettingLoaded: true,
            })
        },
        [isAdminAccess, isBPMReviewer]
    )

    const fetchTableSettings = useCallback(() => {
        getTableSettings()

        const loadTimeout = setTimeout(() => {
            clearTimeout(loadTimeout)
        }, 1000)
    }, [])

    const resetTableSettings = useCallback(() => {
        saveTableSettings({
            variables: {
                input: {
                    ...defaultTableSettings,
                    userId,
                },
            },
        })
    }, [userId, defaultTableSettings, saveTableSettings])

    const saveTableViewSettings = useCallback(
        (state: TableSettingsState) => {
            if (!isTableSettingLoaded) {
                return
            }

            const {
                groupColumns: groupColumnsTemp,
                selectedColumns: selectedColumnsTemp,
                orderBy: orderByTemp,
                groupEnabled: groupEnabledTemp,
                collapseColumnChecked: collapseColumnCheckedTemp,
                columnWidths: columnWidthsTemp,
                pinningProps: pinningPropsTemp,
                isTableView,
            } = state

            const hasGroupColumns = !!groupColumnsTemp.length
            const groupColumnReq = hasGroupColumns
                ? [
                      {
                          columnName: 'groupColExpander',
                          order: 1,
                          columnOrder: 'UNDEFINED',
                          columnWidth:
                              columnWidthsTemp?.['groupColExpander'] ?? 300,
                          isColumnPinned: pinningPropsTemp.firstColumnPinned,
                      },
                  ]
                : []
            const orderByArray = orderByTemp.split(',')

            const selectedColumnsReq = [
                ...groupColumnReq,
                ...selectedColumnsTemp.map(
                    (col: IdTitleProps, index: number) => {
                        const columnOrderObject = orderByArray.find(
                            (val: string) => val.split(' ')[0] === col.id
                        )
                        return {
                            columnName: col.id,
                            order: hasGroupColumns ? index + 2 : index + 1,
                            columnOrder: columnOrderObject
                                ? columnOrderObject.split(' ').length > 1
                                    ? 'DESCENDING'
                                    : 'ASCENDING'
                                : 'UNDEFINED',
                            columnWidth: columnWidthsTemp?.[col.id] ?? 300,
                            isColumnPinned:
                                (!hasGroupColumns &&
                                    index === 0 &&
                                    pinningPropsTemp.firstColumnPinned) ||
                                (hasGroupColumns &&
                                    selectedColumnsTemp.length - 1 === index &&
                                    pinningPropsTemp.lastColumnPinned),
                        }
                    }
                ),
            ]

            if (!!selectedColumnsReq.length) {
                saveTableSettings({
                    variables: {
                        input: {
                            isTableGroupingEnabled: groupEnabledTemp,
                            isTableView,
                            selectedColumns: selectedColumnsReq,
                            tableGroupings: [
                                ...groupColumnsTemp?.map(
                                    (col: IdTitleProps) => ({
                                        columnName: col.id,
                                        isCollapsed:
                                            collapseColumnCheckedTemp?.[col.id],
                                    })
                                ),
                            ],
                            userId,
                        },
                    },
                })
            }
        },
        [isCardViewActive, userId, saveTableSettings, isTableSettingLoaded]
    )

    return {
        isCardViewActive,
        setToggleCardView,
        getListings,
        searchData,
        searchFetchMore,
        searchCalled,
        listingLoading,
        listingNetworkStatus,
        searchLoading,
        setSearchLoading,
        showBottomLoadingIndicator,
        setShowBottomLoadingIndicator,
        showTableLoadingIndicator,
        setShowTableLoadingIndicator,
        fetchTableSettings,
        saveTableViewSettings,
        resetTableSettings,
    }
}

export default useListingQueryHelper
